const normalizeName = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\b(incorporated|inc|llc|ltd|limited|corp|corporation|co|company)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeNumber = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const amountMatches = (a, b) => {
  if (a === null || a === undefined || a === '') return false;
  if (b === null || b === undefined || b === '') return false;

  const first = Number(a);
  const second = Number(b);

  return Number.isFinite(first) &&
    Number.isFinite(second) &&
    Math.abs(first - second) <= 0.01;
};

export function findMatchingBills({
  receipt,
  bills = [],
  vendors = [],
  organizationId
}) {
  if (!receipt || !organizationId) return [];

  const extracted = receipt.extracted_data || {};

  const detectedVendor =
    receipt.extracted_vendor_name ||
    extracted.vendor_name ||
    '';

  const receiptVendorId = receipt.vendor_id || null;

  const documentNumber =
    receipt.document_number ||
    extracted.document_number ||
    '';

  const receiptAmount =
    receipt.amount ?? extracted.total_amount;

  const receiptCurrency =
    receipt.currency ||
    extracted.currency ||
    '';

  const vendorMap = Object.fromEntries(
    vendors.map(vendor => [vendor.id, vendor])
  );

  const normalizedDetectedVendor = normalizeName(detectedVendor);
  const normalizedDocumentNumber = normalizeNumber(documentNumber);

  const results = [];

  for (const bill of bills) {
    if (bill.organization_id !== organizationId) continue;

    const billVendor = vendorMap[bill.vendor_id];

    const vendorIdMatch = Boolean(
      receiptVendorId &&
      bill.vendor_id === receiptVendorId
    );

    const vendorNameMatch = Boolean(
      normalizedDetectedVendor &&
      billVendor &&
      (
        normalizeName(billVendor.vendor_name) === normalizedDetectedVendor ||
        normalizeName(billVendor.company_name) === normalizedDetectedVendor
      )
    );

    const vendorMatch = vendorIdMatch || vendorNameMatch;

    const numberMatch = Boolean(
      normalizedDocumentNumber &&
      normalizeNumber(bill.bill_number) === normalizedDocumentNumber
    );

    const totalMatch = amountMatches(
      receiptAmount,
      bill.total
    );

    const currencyMatch = Boolean(
      receiptCurrency &&
      bill.currency &&
      receiptCurrency.toUpperCase() === bill.currency.toUpperCase()
    );

    let score = 0;

    if (vendorMatch) score += 40;
    if (numberMatch) score += 35;
    if (totalMatch) score += 20;
    if (currencyMatch) score += 5;

    // Require strong evidence before suggesting a match.
    if (!vendorMatch && !numberMatch) continue;

    // Reject bills with a clear vendor conflict.
    if (receiptVendorId && bill.vendor_id !== receiptVendorId) {
      continue;
    }

    const warnings = [];

    if (!vendorMatch) {
      warnings.push('Vendor does not match');
    }

    if (
      receiptAmount !== null &&
      receiptAmount !== undefined &&
      !totalMatch
    ) {
      warnings.push('Amount differs');
    }

    if (
      receiptCurrency &&
      bill.currency &&
      !currencyMatch
    ) {
      warnings.push('Currency differs');
    }

    results.push({
      bill,
      score,
      vendorMatch,
      numberMatch,
      totalMatch,
      currencyMatch,
      warnings,
      confidence:
        score >= 90
          ? 'high'
          : score >= 60
            ? 'medium'
            : 'low'
    });
  }

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}